import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useAddLuxerLocation, useRemoveLuxerLocation, useUpdateApartmentsId } from '@/api/building'
import { FieldsTable } from '@/components/layout/field-row'
import { TabActions, TabCard } from '@/components/layout/tab-card'
import { toastFactory } from '@/factories'
import { DirtyFieldChange } from '@/hooks'
import { Building } from '@/reducers/types'
import { getApiErrorMessage } from '@/utils/error'

import { FormChangeFields, getFormChanges } from '../../../../shared/form-changes'
import { PlaceFlags } from '../../../features/places-features.constants'
import { buildFlagsPayload, toFlags } from '../../../features/places-features.rules'
import { PlaceFeatureRow } from '../../flags/place-feature-row'
import { usePlaceSave } from '../../use-place-save'
import { ApartmentsIdEdit } from './apartments-id-edit'
import { ApartmentsIdView } from './apartments-id-view'
import { LuxerLockersEdit } from './luxer-lockers-edit'
import { LuxerLockersView } from './luxer-lockers-view'
import { getLockers, getOtherDefaults, OtherFormData } from './other-tab.constants'
import { ParcelPendingUrlView } from './parcel-pending-url-view'
import { SaltoSpaceCache } from './salto-space-cache'

interface OtherTabProps {
  building: Building
  isAdmin: boolean
  onDirtyChange: (isDirty: boolean, getChanges?: () => DirtyFieldChange[]) => void
}

export const OtherTab = ({ building, isAdmin, onDirtyChange }: OtherTabProps) => {
  const { t } = useTranslation(['organisation'])
  const { save: saveFlags } = usePlaceSave(building)
  const { mutateAsync: addLocker } = useAddLuxerLocation()
  const { mutateAsync: removeLocker } = useRemoveLuxerLocation()
  const { mutateAsync: updateApartmentsId } = useUpdateApartmentsId()
  const [isEditing, setIsEditing] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [flagError, setFlagError] = useState<string>()

  const serverValues = useMemo(() => getOtherDefaults(building), [building])

  const form = useForm<OtherFormData>({
    defaultValues: serverValues,
    // Note: a background update of the building must not wipe what the user is editing
    resetOptions: { keepDirtyValues: true },
    values: serverValues,
  })

  const { isDirty } = form.formState

  const changeFields = useMemo<FormChangeFields<OtherFormData>>(
    () => ({
      apartmentsId: { label: t('organisation:places.other.apartments.title') },
      luxerIds: {
        items: (value) => value as string[],
        label: t('organisation:places.other.luxer.title'),
      },
      parcelPendingAvailable: {
        format: (value) =>
          value ? t('organisation:places.save.on') : t('organisation:places.save.off'),
        label: t('organisation:places.building.toggles.parcelPendingAvailable'),
      },
    }),
    [t],
  )

  const getChanges = useCallback(
    () =>
      getFormChanges({
        defaultValues: form.formState.defaultValues,
        fields: changeFields,
        values: form.getValues(),
      }),
    [changeFields, form],
  )

  useEffect(() => {
    const isDirtyForm = isEditing && isDirty
    onDirtyChange(isDirtyForm, isDirtyForm ? getChanges : undefined)
  }, [isEditing, isDirty, getChanges, onDirtyChange])

  const handleCancel = () => {
    form.reset(serverValues)
    setFlagError(undefined)
    setIsEditing(false)
  }

  const saveFlag = async (next: boolean) => {
    const nextFlags = { ...toFlags(building), parcelPendingAvailable: next } as PlaceFlags
    const result = await saveFlags({
      nextFlags,
      payload: buildFlagsPayload({ isAdmin, next: nextFlags, server: building }),
    })
    setFlagError(!result.ok && 'error' in result ? result.message : undefined)
    return result.ok
  }

  const saveLockers = async (next: string[], onSaved: VoidFunction) => {
    const lockers = getLockers(building)
    const current = lockers.map((locker) => String(locker.luxerOneId))

    for (const locker of lockers) {
      if (!next.includes(String(locker.luxerOneId))) {
        await removeLocker({ buildingId: building.id, luxerId: locker.id })
        onSaved()
      }
    }
    for (const id of next) {
      if (!current.includes(id)) {
        await addLocker({ buildingId: building.id, luxerOneId: Number(id) })
        onSaved()
      }
    }
  }

  const handleSave = form.handleSubmit(async (next) => {
    setIsSaving(true)
    // Note: the three requests are separate endpoints with no rollback, so a failure can leave the
    // earlier ones applied - the user is told when that happened
    let hasSavedPart = false
    const markSaved = () => {
      hasSavedPart = true
    }
    const partialNote = () =>
      hasSavedPart ? ` ${t('organisation:places.other.toasts.partiallySaved')}` : ''

    try {
      if (next.parcelPendingAvailable !== serverValues.parcelPendingAvailable) {
        if (!(await saveFlag(next.parcelPendingAvailable))) return
        markSaved()
      }

      await saveLockers(next.luxerIds, markSaved)

      const apartmentsId = next.apartmentsId.trim()
      if (isAdmin && apartmentsId && apartmentsId !== serverValues.apartmentsId) {
        try {
          await updateApartmentsId({ buildingId: building.id, locationId: apartmentsId })
          toastFactory.success(t('organisation:places.apartmentsId.toasts.updated'))
        } catch (error) {
          form.setError('apartmentsId', { message: `${getApiErrorMessage(error)}${partialNote()}` })
          return
        }
      }

      form.reset({ ...next, apartmentsId: isAdmin ? apartmentsId : serverValues.apartmentsId })
      setIsEditing(false)
    } catch (error) {
      toastFactory.error(`${getApiErrorMessage(error)}${partialNote()}`)
    } finally {
      setIsSaving(false)
    }
  })

  return (
    <FormProvider {...form}>
      <TabCard
        actions={
          <TabActions
            isDirty={isDirty}
            isEditing={isEditing}
            isSaving={isSaving}
            onCancel={handleCancel}
            onEdit={() => setIsEditing(true)}
            onSave={handleSave}
          />
        }
        title={t('organisation:places.other.title')}
      >
        <FieldsTable>
          <PlaceFeatureRow
            building={building}
            error={flagError}
            featureKey="parcelPendingAvailable"
            isAdmin={isAdmin}
            isEditing={isEditing}
            layout="field"
          />
          <ParcelPendingUrlView building={building} />
          {isEditing ? (
            <>
              <LuxerLockersEdit />
              <ApartmentsIdEdit building={building} isAdmin={isAdmin} />
            </>
          ) : (
            <>
              <LuxerLockersView values={serverValues} />
              <ApartmentsIdView building={building} isAdmin={isAdmin} values={serverValues} />
            </>
          )}
          <SaltoSpaceCache building={building} isAdmin={isAdmin} />
        </FieldsTable>
      </TabCard>
    </FormProvider>
  )
}
